import {
  Menu, Bell, BellOff, Globe,
  ChevronDown, LogOut, Settings, User, Check, Trash2
} from 'lucide-react'
import { useUIStore } from '@/store/useUIStore'
import { useAuthStore } from '@/store/useAuthStore'
import { useNotificationStore } from '@/store/useNotificationStore'
import { DUMMY_AVATAR, onAvatarError } from '@/shared/utils'
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'

// Compact "x ago" formatter for the bell dropdown timestamps.
function timeAgo(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (secs < 60) return t('header.notifications.justNow')
  const mins = Math.floor(secs / 60)
  if (mins < 60) return t('header.notifications.minutesAgo', { count: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24) return t('header.notifications.hoursAgo', { count: hours })
  const days = Math.floor(hours / 24)
  return t('header.notifications.daysAgo', { count: days })
}

export function Header() {
  const { t } = useTranslation()
  const toggleSidebar = useUIStore(s => s.toggleSidebar)
  const { user, logout } = useAuthStore()
  const { notifications, unreadCount, markAllRead, remove, clear } = useNotificationStore()
  const isSchoolAdmin = user?.user_type === 'school_admin'
  const [menuOpen, setMenuOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [bellOpen, setBellOpen] = useState(false)
  const [lang, setLang] = useState(i18n.language)
  const navigate = useNavigate()
  const menuRef = useRef<HTMLDivElement>(null)
  const langRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false)
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggleBell = () => {
    setBellOpen(o => {
      const next = !o
      if (next && unreadCount > 0) markAllRead()
      return next
    })
  }

  const LANGUAGES = [
    { code: 'en', label: 'English', native: 'English' },
    { code: 'ar', label: 'Arabic',  native: 'العربية' },
  ]
  const current = LANGUAGES.find(l => l.code === lang) ?? LANGUAGES[0]

  const changeLang = (code: string) => {
    i18n.changeLanguage(code)
    localStorage.setItem('drivys-lang', code)
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr'
    setLang(code)
    setLangOpen(false)
  }

  return (
    <header className="h-[62px] bg-white border-b border-gray-100 flex items-center px-3 sm:px-5 gap-2 sm:gap-3 flex-shrink-0 z-10 shadow-sm">
      {/* Hamburger */}
      <button
        onClick={toggleSidebar}
        className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors flex-shrink-0"
        aria-label="Toggle sidebar"
      >
        <Menu size={20} />
      </button>

      <div className="flex-1" />

      {/* Right actions */}
      <div className="flex items-center gap-0.5 sm:gap-1">

        {/* Notifications bell */}
        <div className="relative" ref={bellRef}>
          <button
            onClick={toggleBell}
            className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${bellOpen ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
            aria-label={t('header.notifications.title')}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-4 h-4 px-1 bg-red-500 rounded-full text-white text-[11px] flex items-center justify-center font-bold leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {bellOpen && (
            <div className="absolute rtl:left-0 ltr:right-0 top-full mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <span className="text-[14px] font-bold text-gray-900">{t('header.notifications.title')}</span>
                {notifications.length > 0 && (
                  <button
                    onClick={clear}
                    className="flex items-center gap-1 text-[12px] font-semibold text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={13} /> {t('header.notifications.clearAll')}
                  </button>
                )}
              </div>

              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-gray-400">
                  <BellOff size={28} className="text-gray-300" />
                  <span className="text-[13px] font-medium">{t('header.notifications.empty')}</span>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map(n => (
                    <div
                      key={n.id}
                      className="group flex gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
                        {n.image ? (
                          <img src={n.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Bell size={15} className="text-brand-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-gray-900 leading-snug">{n.title}</div>
                        {n.body && <div className="text-[12px] text-gray-500 leading-snug mt-0.5 break-words">{n.body}</div>}
                        <div className="text-[11px] text-gray-400 mt-1">{timeAgo(n.timestamp, t)}</div>
                      </div>
                      <button
                        onClick={() => remove(n.id)}
                        className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
                        aria-label={t('header.notifications.dismiss')}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Language selector */}
        <div className="relative" ref={langRef}>
          <button
            onClick={() => setLangOpen(o => !o)}
            className={`flex items-center gap-1.5 sm:gap-2 h-9 pl-2 sm:pl-2.5 pr-1.5 sm:pr-2 rounded-lg border transition-colors ${langOpen ? 'bg-gray-100 border-gray-300 text-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'}`}
            title="Change language"
          >
            <Globe size={16} className="text-gray-500" />
            <span className="hidden sm:inline text-[14px] font-semibold">{current.native}</span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
          </button>

          {langOpen && (
            <div className="absolute right-0 top-full mt-2 w-44 max-w-[calc(100vw-1.5rem)] bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50">
              <div className="px-3 pb-1.5 mb-1 border-b border-gray-100 text-[12px] font-bold text-gray-400 uppercase tracking-wider">Select Language</div>
              {LANGUAGES.map(l => (
                <button
                  key={l.code}
                  onClick={() => changeLang(l.code)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <span className="flex-1 text-left font-medium">{l.native}</span>
                  {l.code === lang && <Check size={15} className="text-brand-500" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="hidden sm:block w-px h-7 bg-gray-200 mx-2" />

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-2.5 hover:bg-gray-100 rounded-lg px-2 py-1.5 transition-colors"
          >
            {/* Account avatar — dummy silhouette when no photo */}
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src={user?.photo_url || DUMMY_AVATAR} alt={user?.name ?? 'Account'} onError={onAvatarError}
                className="w-full h-full object-cover rounded-lg" />
            </div>
            <div className="text-left hidden sm:block">
              <div className="text-[14px] font-semibold text-gray-900 leading-tight">{user?.name || 'Wade Warren'}</div>
              <div className="text-[13px] text-gray-500 leading-tight truncate max-w-[140px]">{user?.email || 'admin@drivys.com'}</div>
            </div>
            <ChevronDown size={15} className={`text-gray-400 transition-transform flex-shrink-0 ${menuOpen ? 'rotate-180' : ''}`} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-48 max-w-[calc(100vw-1.5rem)] bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50">
              <div className="px-4 py-2.5 border-b border-gray-100">
                <div className="text-[14px] font-semibold text-gray-900">{user?.name}</div>
                <div className="text-[13px] text-gray-400 truncate">{user?.email}</div>
              </div>
              {/* Profile / Settings link to admin-only routes — hidden for school admins */}
              {!isSchoolAdmin && (
                <>
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/dashboard/user/list') }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User size={14} className="text-gray-400" /> {t('header.profile')}
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/dashboard/system/app-settings') }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings size={14} className="text-gray-400" /> {t('header.settings')}
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                </>
              )}
              <button
                onClick={() => { logout(); navigate(isSchoolAdmin ? '/login' : '/login?type=admin') }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
              >
                <LogOut size={14} /> {t('header.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
